import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { NetworkViewType } from '../../typings/graphql-log';
import { NetworkGroups } from '../../typings/log';
import {
    ExpandableCell,
    ExpandableProps
} from '../ExpandableCell/ExpandableCell';

import './NetworkCell.css';
import * as keyset from './NetworkCell.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

export interface NetworkProps extends ExpandableProps {
    diff: NetworkGroups;
}

export class NetworkCell extends ExpandableCell<NetworkProps> {
    protected getContent() {
        const {
                diff: { before }
            } = this.props,
            networkWasOff = before.type === NetworkViewType.disabled;

        let block: React.ReactNode;
        if (networkWasOff) {
            block = this.getNetworkEnabled();
        } else {
            block = this.getNetworkDiff();
        }

        return (
            <div
                className={Bem.cls('network-cell')}
                ref={ref => (this.cell = ref)}
            >
                {block}
            </div>
        );
    }

    private getNetworkEnabled = (): React.ReactNode => {
        const {
            diff: { after }
        } = this.props;

        return (
            <div
                className={classNames(
                    Bem.cls('network-cell', 'added'),
                    Bem.cls('network-cell', 'row')
                )}
            >
                <div>
                    {this.getTextDiv(
                        i18n('campaign_context_limit_not_changed', {
                            limitPercent: after.limitPercent
                        })
                    )}
                    {this.getTextDiv(
                        i18n('campaign_context_price_coef_not_changed', {
                            priceCoef: after.priceCoef
                        })
                    )}
                    {after.enableCpcHold
                        ? this.getTextDiv(
                              i18n('campaign_context_enable_cpc_hold')
                          )
                        : ''}
                </div>
            </div>
        );
    };

    private getNetworkDiff = (): React.ReactNode => {
        return (
            <div>
                <div className={Bem.cls('network-cell', 'row')}>
                    {this.getLimitPercentDiff()}
                    {this.getPriceCoefDiff()}
                    {this.getEnableCpcDiff()}
                </div>
            </div>
        );
    };

    private getLimitPercentDiff = (): React.ReactNode => {
        const {
                diff: { before, after }
            } = this.props,
            beforeValue = before.limitPercent,
            afterValue = after.limitPercent,
            isEqual = beforeValue === afterValue;

        let span;

        if (isEqual) {
            span = i18n('campaign_context_limit_not_changed', {
                limitPercent: `${afterValue}`
            });
        } else {
            const oldValueSpan = (
                    <span
                        key={'old_limit'}
                        className={Bem.cls('network-cell', 'removed')}
                    >
                        {beforeValue}%
                    </span>
                ),
                newValueSpan = (
                    <span
                        key={'new_limit'}
                        className={Bem.cls('network-cell', 'added')}
                    >
                        {afterValue}%
                    </span>
                );
            span = i18nRaw('campaign_context_limit', {
                oldLimitPercent: oldValueSpan,
                newLimitPercent: newValueSpan
            });
        }

        return this.getTextDiv(span, 'limit-percent-diff');
    };

    private getPriceCoefDiff = (): React.ReactNode => {
        const {
                diff: { before, after }
            } = this.props,
            beforeValue = before.priceCoef,
            afterValue = after.priceCoef,
            isEqual = beforeValue === afterValue;

        let span;

        if (isEqual) {
            span = i18n('campaign_context_price_coef_not_changed', {
                priceCoef: `${afterValue}`
            });
        } else {
            const oldValueSpan = (
                    <span
                        key={'old_price_coef'}
                        className={Bem.cls('network-cell', 'removed')}
                    >
                        {beforeValue}%
                    </span>
                ),
                newValueSpan = (
                    <span
                        key={'new_price_coef'}
                        className={Bem.cls('network-cell', 'added')}
                    >
                        {afterValue}%
                    </span>
                );
            span = i18nRaw('campaign_context_price_coef', {
                oldPriceCoef: oldValueSpan,
                newPriceCoef: newValueSpan
            });
        }

        return this.getTextDiv(span, 'price-coef-diff');
    };

    private getEnableCpcDiff = (): React.ReactNode => {
        const {
                diff: { before, after }
            } = this.props,
            beforeValue = before.enableCpcHold,
            afterValue = after.enableCpcHold,
            isEqual = beforeValue === afterValue;

        const span = (
            <span
                className={Bem.cls(
                    'network-cell',
                    isEqual ? 'not-modified' : beforeValue ? 'removed' : 'added'
                )}
            >
                {i18n('campaign_context_enable_cpc_hold')}
            </span>
        );

        return this.getTextDiv(span, 'enable-cpc-diff');
    };

    private getTextDiv = (
        text: React.ReactNode,
        key?: string
    ): React.ReactNode => {
        const { isExpanded, canExpand } = this.props;

        return (
            <div
                key={key}
                className={classNames(
                    Bem.cls('network-cell', 'cell'),
                    !isExpanded && canExpand
                        ? Bem.cls('expandable-cell', 'short-row')
                        : ''
                )}
            >
                {text}
            </div>
        );
    };
}
